/**
 * RIM — IRA Distribution Flow (5-step wizard).
 *
 * Port target:
 *   - Legacy SPFx `IraManagementScreen.tsx` + `iraFinancialDisclosure.ts`
 *     — reason/withholding/delivery semantics, 1099-R year stamping.
 *   - `JxChangeService.taxPlnMod` — body envelope + AccountId tail.
 *
 * Steps:
 *   1. Reason code (typed config — `client.retail.config.iraReasonCodes()`).
 *   2. Amount (gross) + tran code derived from reason.
 *   3. Tax withholding (fed/state %).
 *   4. Delivery method (check, ACH, wire, internal).
 *   5. 1099-R year + review → `TaxPlnTranAdd` post.
 *
 * Wave 5.1 (Agent IRA-FN-REASON-CODES) — `FALLBACK_REASONS` removed.
 * Reason codes are sourced from `dbo.IraReasonCodes` via
 * `GET /v3/config/ira-reason-codes`. If the route 503s the wizard
 * surfaces an explicit error banner — never a silent hardcoded fallback.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { ApiError } from '@retail-ops/api-client';
import type {
  IraDistributionDelivery,
  IraDistributionPostedData,
  IraReasonCode,
} from '@retail-ops/shared-types';
import { Banner, Button, CardRadioGroup, KVPair, Stepper, WidgetCard } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import { IRA_TXN_CODES } from '../constants';
import '../styles/rim.css';

type Delivery = 'check' | 'ach' | 'wire' | 'internal';

/**
 * Map the wizard's UX-friendly delivery code to the server contract value.
 * Wave 5.2 IRA-FN-DISTRIBUTIONS — IraFunction.PostDistribution accepts
 * the canonical `IraDistributionDelivery` union below; the wizard keeps
 * the legacy short codes for visual parity with the existing radio-card
 * options. `wire` and `internal` map 1:1; the rest collapse to the
 * "to-owner" delivery (third-party + ACH delivery is a future polish).
 */
function toServerDelivery(d: Delivery): IraDistributionDelivery {
  switch (d) {
    case 'check': return 'CHECK_TO_OWNER';
    case 'ach': return 'ACH_TO_OWNER';
    case 'wire': return 'WIRE';
    case 'internal': return 'INTERNAL_TRANSFER';
  }
}

interface ReasonDef {
  code: string;
  label: string;
  description: string;
  /** Preferred tran code for this reason (withdrawal-kind). */
  tranCode: string;
}

interface DistDraft {
  reasonCode: string | null;
  amount: string;
  tranCode: string;
  fedPct: string;
  statePct: string;
  delivery: Delivery;
  form1099Year: number;
  /** Wave 5.2 — officer attestation for reasons w/ requiresExceptionDocumentation. */
  exceptionDocumentationOnFile: boolean;
  /** Optional destination institution code for Q3 penalty-exempt detection. */
  deliveryAccount: string;
}

const STEPS = [
  { label: 'Reason code' },
  { label: 'Amount' },
  { label: 'Tax withholding' },
  { label: 'Delivery' },
  { label: '1099-R & submit' },
];

/**
 * Reason-code → preferred Core TC mapping. Lives client-side because the
 * v3 `IraReasonCode` entity (per Bill 2026-04-24 column spec) does not
 * carry a `TransactionCode` column. Add one to the entity + seed if/when
 * Emily/Collette want this configurable per-row; until then the wizard
 * derives the default tran from the reason code and the officer can
 * override on step 2.
 */
const REASON_TO_TRAN: Readonly<Record<string, string>> = {
  NORMAL: '631',
  EARLY_NOEXC: '641',
  EARLY_EXC_72T: '651',
  DEATH: '654',
  DISABILITY: '641',
  QDRO: '641',
  PROHIBITED: '641',
  ROLLOVER_60DAY: '631',
  ROLLOVER_DIRECT: '633',
  EXCESS_REMOVAL: '641',
  RECHAR: '641',
  CONVERSION: '631',
  INHERITED_RMD: '654',
  INHERITED_DEPLETE: '654',
};

function configRowToReasonDef(r: IraReasonCode): ReasonDef {
  return {
    code: r.code,
    label: r.displayName,
    description: r.notes ?? '',
    tranCode: REASON_TO_TRAN[r.code] ?? '631',
  };
}

export function DistributionFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<DistDraft>({
    reasonCode: null,
    amount: '',
    tranCode: '631',
    fedPct: '10',
    statePct: '0',
    delivery: 'check',
    form1099Year: new Date().getFullYear(),
    exceptionDocumentationOnFile: false,
    deliveryAccount: '',
  });
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | null>(null);
  /** Wave 5.2 — server-computed confirmation payload (1099-R box, withholding, net). */
  const [submitResult, setSubmitResult] = useState<IraDistributionPostedData | null>(null);
  /** Raw `IraReasonCode` rows (kept beside the `ReasonDef` projection so the
   *  flow can read `requiresExceptionDocumentation` + `form1099Box7Code`). */
  const [reasonRows, setReasonRows] = useState<ReadonlyArray<IraReasonCode>>([]);
  const [configReasons, setConfigReasons] = useState<ReadonlyArray<ReasonDef> | null>(null);
  const [reasonsLoading, setReasonsLoading] = useState<boolean>(true);
  const [reasonsError, setReasonsError] = useState<string | null>(null);

  // Reason codes — typed via `client.retail.config.iraReasonCodes()`
  // (Wave 5.1 / Agent IRA-FN-REASON-CODES). Backed by dbo.IraReasonCodes
  // and seeded from `09-ira-reason-codes.sql`. No silent fallback — if
  // the route fails, the officer sees an explicit error banner.
  useEffect(() => {
    let cancelled = false;
    setReasonsLoading(true);
    setReasonsError(null);
    void (async (): Promise<void> => {
      try {
        const resp = await client.retail.config.iraReasonCodes();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setReasonRows(resp.data);
          setConfigReasons(resp.data.map(configRowToReasonDef));
        } else {
          setReasonsError(resp.error ?? 'Reason codes unavailable.');
        }
      } catch (err) {
        if (cancelled) return;
        setReasonsError(
          ApiError.isApiError(err) ? err.message : err instanceof Error ? err.message : String(err)
        );
      } finally {
        if (!cancelled) setReasonsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client]);

  const reasons = configReasons ?? [];

  useEffect(() => {
    if (!draft.reasonCode) return;
    const r = reasons.find((x) => x.code === draft.reasonCode);
    if (r && r.tranCode !== draft.tranCode) setDraft((d) => ({ ...d, tranCode: r.tranCode }));
  }, [draft.reasonCode, reasons, draft.tranCode]);

  const amountNum = Number.parseFloat(draft.amount);
  const fedNum = Number.parseFloat(draft.fedPct);
  const stateNum = Number.parseFloat(draft.statePct);
  const chosenReason = useMemo(() => reasons.find((r) => r.code === draft.reasonCode) ?? null, [reasons, draft.reasonCode]);
  /** Raw IraReasonCode row for the currently-selected reason — drives the
   *  exception-documentation gate + form 1099-R box-code preview. */
  const selectedReason = useMemo(
    () => reasonRows.find((r) => r.code === draft.reasonCode) ?? null,
    [reasonRows, draft.reasonCode]
  );
  const requiresExceptionDocs = selectedReason?.requiresExceptionDocumentation === true;

  if (!planNumber) return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before starting a distribution." />;
  if (loadingIra) return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const canProceed = ((): boolean => {
    switch (step) {
      case 0: return !!chosenReason;
      case 1: return Number.isFinite(amountNum) && amountNum > 0;
      case 2: return Number.isFinite(fedNum) && fedNum >= 0 && fedNum <= 100 && Number.isFinite(stateNum) && stateNum >= 0 && stateNum <= 100;
      case 3: return !!draft.delivery;
      case 4: {
        const yearOk = Number.isInteger(draft.form1099Year) && draft.form1099Year >= 1900;
        const stateOk = submitState === 'idle' || submitState === 'error';
        // Exception-doc attestation gate — server returns MISSING_EXCEPTION_DOCS if false.
        const docsOk = !requiresExceptionDocs || draft.exceptionDocumentationOnFile;
        return yearOk && stateOk && docsOk;
      }
      default: return false;
    }
  })();

  /**
   * Wave 5.2 IRA-FN-DISTRIBUTIONS — server-validated post.
   * Replaces the legacy direct-jXRL call (`IraCoreService.postDistribution`)
   * with `client.retail.ira.postDistribution()`. The Function evaluates plan
   * status, IraReasonCode activation, exception-doc attestation, balance
   * sufficiency, IraInstitutionRelationship penalty-exempt detection,
   * withholding defaults, IraTransactionCode scenario lookup, computes the
   * canonical fed/state withholding amounts + 1099-R box code, then posts
   * via TaxPlnTranAdd. The server payload populates the confirmation
   * summary directly; the client never recomputes net amount or box code.
   */
  const submit = async (): Promise<void> => {
    if (!chosenReason) {
      setSubmitError('Reason code missing — cannot post.');
      setSubmitErrorCode(null);
      setSubmitState('error');
      return;
    }
    const officerCode = (user?.officerCode ?? user?.upn ?? '').trim();
    if (!officerCode) {
      setSubmitError('Officer code unavailable — sign in as an officer to post distributions.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }

    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    setSubmitResult(null);
    try {
      const resp = await client.retail.ira.postDistribution({
        planNumber: ira.planNumber,
        reasonCode: chosenReason.code,
        amount: amountNum,
        fedWithholdingPct: fedNum,
        stateWithholdingPct: stateNum,
        delivery: toServerDelivery(draft.delivery),
        ...(draft.deliveryAccount ? { deliveryAccount: draft.deliveryAccount } : {}),
        officerCode,
        exceptionDocumentationOnFile: draft.exceptionDocumentationOnFile,
      });

      if (!resp.success) {
        setSubmitError(resp.error);
        setSubmitErrorCode(resp.code);
        setSubmitState('error');
        return;
      }

      setSubmitResult(resp.data);
      emit({
        eventType: 'ira-distribution-posted',
        eventCategory: 'cif-account',
        severity: 'warn',
        context: `plan:${ira.planNumber}`,
        payload: {
          planNumber: ira.planNumber,
          planCode: ira.planCode,
          reasonCode: chosenReason.code,
          tranCode: resp.data.transactionCode,
          amount: resp.data.postedAmount,
          fedAmount: resp.data.fedWithholdingAmount,
          stateAmount: resp.data.stateWithholdingAmount,
          netAmount: resp.data.netAmount,
          form1099BoxCode: resp.data.form1099BoxCode,
          delivery: draft.delivery,
          taxYear: resp.data.taxYear,
          penaltyExempt: resp.data.penaltyExempt,
          scenario: resp.data.scenario,
        },
      });
      setSubmitState('submitted');
    } catch (err) {
      const msg = ApiError.isApiError(err) ? err.message : err instanceof Error ? err.message : String(err);
      setSubmitError(msg || 'Distribution post failed.');
      setSubmitErrorCode(null);
      setSubmitState('error');
    }
  };

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>IRA distribution · {ira.planDescription ?? ira.planCodeRaw} · {ira.planNumber}</h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Reason for distribution" stripe="compliance">
          <p className="rim-flow__helper">
            Reason codes are administered in <em>Admin → Reference → IRA Reason Codes</em> and
            served from <code>/v3/config/ira-reason-codes</code>. Each row maps a plain-language
            reason to an IRS Form 1099-R box 7 distribution code.
          </p>
          {reasonsLoading && (
            <div className="rim-flow__loading">
              <LoadingSpinner label="Loading reason codes…" size="md" />
            </div>
          )}
          {!reasonsLoading && reasonsError && (
            <Banner tone="err" title="Reason codes unavailable">
              {reasonsError}. Contact IT — the officer cannot post a distribution without a
              configured reason code.
            </Banner>
          )}
          {!reasonsLoading && !reasonsError && reasons.length === 0 && (
            <Banner tone="warn" title="No active reason codes">
              The catalog is empty. An admin must seed at least one active row before
              distributions can be posted.
            </Banner>
          )}
          {!reasonsLoading && !reasonsError && reasons.length > 0 && (
            <CardRadioGroup<string>
              name="rim-dist-reason"
              selected={draft.reasonCode}
              onChange={(v) => setDraft({ ...draft, reasonCode: v })}
              options={reasons.map((r) => ({
                value: r.code,
                primary: r.label,
                helper: r.description ? `${r.description} · tran ${r.tranCode}` : `tran ${r.tranCode}`,
              }))}
            />
          )}
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Gross distribution amount" stripe="compliance">
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Amount (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.amount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, amount: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Tran code</span>
              <select
                className="rim-benef-field"
                value={draft.tranCode}
                onChange={(e) => setDraft({ ...draft, tranCode: e.currentTarget.value })}
                aria-label="Tran code"
              >
                {IRA_TXN_CODES.filter((t) => t.kind === 'withdrawal').map((t) => (
                  <option key={t.code} value={t.code}>{t.code} · {t.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="rim-flow__hint">
            Current plan balance (Core Director): <strong>{ira.balance != null ? `$${ira.balance.toFixed(2)}` : '—'}</strong>
          </div>
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Tax withholding" stripe="compliance">
          <p className="rim-flow__helper">
            Fed default is 10% for standard distributions; 0% allowed with W-4R election. State overrides live
            in Core Director product setup — officer can adjust per owner request.
          </p>
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Federal %</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.fedPct}
                min={0}
                max={100}
                step={0.5}
                onChange={(e) => setDraft({ ...draft, fedPct: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">State %</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.statePct}
                min={0}
                max={100}
                step={0.5}
                onChange={(e) => setDraft({ ...draft, statePct: e.currentTarget.value })}
              />
            </label>
          </div>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Delivery method" stripe="compliance">
          <CardRadioGroup<Delivery>
            name="rim-dist-delivery"
            selected={draft.delivery}
            onChange={(v) => setDraft({ ...draft, delivery: v })}
            options={[
              { value: 'check',    primary: 'Check',              helper: 'Mailed / picked up at branch.' },
              { value: 'ach',      primary: 'ACH deposit',        helper: 'Standing ACH instruction required.' },
              { value: 'wire',     primary: 'Wire',               helper: 'Same-day, fees may apply.' },
              { value: 'internal', primary: 'Internal transfer',  helper: 'To another PCB deposit account.' },
            ]}
          />
          <label className="rim-flow__field rim-flow__field--gap-top">
            <span className="rim-flow__field-label">Destination institution code (optional)</span>
            <input
              type="text"
              className="rim-benef-field"
              value={draft.deliveryAccount}
              placeholder="e.g. RAYMOND_JAMES_VERNA"
              onChange={(e) => setDraft({ ...draft, deliveryAccount: e.currentTarget.value })}
            />
            <span className="rim-flow__hint">
              Match an admin-configured <code>IraInstitutionRelationship</code> code to skip the
              early-withdrawal penalty calc (e.g. Raymond James / Verna). Leave blank for direct-to-owner.
            </span>
          </label>
        </WidgetCard>
      )}

      {step === 4 && (
        <WidgetCard title="1099-R reporting year & review" stripe="compliance">
          <label className="rim-flow__field">
            <span className="rim-flow__field-label">1099-R year (planned)</span>
            <input
              type="number"
              className="rim-benef-field"
              value={draft.form1099Year}
              min={1900}
              max={9999}
              onChange={(e) => setDraft({ ...draft, form1099Year: Number.parseInt(e.currentTarget.value, 10) || draft.form1099Year })}
            />
            <span className="rim-flow__hint">
              The server stamps the actual tax year on the post (calendar year of submission per
              IRS Pub 590-B); this field is informational for the officer's review.
            </span>
          </label>

          {/*
            Wave 5.2 IRA-FN-DISTRIBUTIONS — exception-documentation gate.
            When the selected reason has `requiresExceptionDocumentation = true`,
            the officer MUST tick the attestation checkbox before posting; the
            server enforces the same rule and returns 422 MISSING_EXCEPTION_DOCS
            if the body field is false.
          */}
          {requiresExceptionDocs && (
            <Banner tone="warn" title="Exception documentation required">
              This reason ({chosenReason?.label ?? draft.reasonCode}) requires supporting
              documentation be on file before the distribution posts. Confirm below.
              <label className="rim-flow__field">
                <input
                  type="checkbox"
                  checked={draft.exceptionDocumentationOnFile}
                  onChange={(e) => setDraft({ ...draft, exceptionDocumentationOnFile: e.currentTarget.checked })}
                />
                {' '}Officer confirms supporting documentation is on file.
              </label>
            </Banner>
          )}

          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="CIF" value={ira.cifId ?? '—'} />
            <KVPair label="Reason" value={chosenReason?.label ?? '—'} />
            <KVPair label="Tran code (preview)" value={draft.tranCode} />
            <KVPair label="Amount" value={`$${amountNum.toFixed(2)}`} />
            <KVPair label="Fed / State w/h %" value={`${fedNum.toFixed(1)}% / ${stateNum.toFixed(1)}%`} />
            <KVPair label="Delivery" value={draft.delivery} />
            {selectedReason?.form1099Box7Code && (
              <KVPair label="1099-R box 7 (preview)" value={selectedReason.form1099Box7Code} />
            )}
            {draft.deliveryAccount && (
              <KVPair label="Destination institution" value={draft.deliveryAccount} />
            )}
          </div>

          {/*
            Server-computed confirmation summary (Wave 5.2). The Function
            response carries the canonical 1099-R box code, computed
            withholding amounts, and net amount — render those here without
            recomputing client-side.
          */}
          {submitState === 'submitted' && submitResult && (
            <>
              <div className="rim-rmd-banner" role="status">
                Distribution posted. Confirmation below — share with the customer.
              </div>
              <div className="rim-kv-grid">
                <KVPair label="Transaction id" value={submitResult.transactionId} />
                <KVPair label="Posted TC" value={submitResult.transactionCode} />
                <KVPair label="Posted amount" value={`$${submitResult.postedAmount.toFixed(2)}`} />
                <KVPair label="Federal withholding" value={`$${submitResult.fedWithholdingAmount.toFixed(2)}`} />
                <KVPair label="State withholding" value={`$${submitResult.stateWithholdingAmount.toFixed(2)}`} />
                <KVPair label="Net to customer" value={`$${submitResult.netAmount.toFixed(2)}`} />
                <KVPair label="1099-R box 7" value={submitResult.form1099BoxCode || '—'} />
                <KVPair label="Tax year stamped" value={submitResult.taxYear} />
                {submitResult.penaltyExempt && (
                  <KVPair label="Penalty exempt" value="Yes (institution carve-out applied)" />
                )}
                <KVPair label="Scenario" value={submitResult.scenario} />
              </div>
            </>
          )}

          {/*
            Friendly server-error rendering. TC_NOT_CONFIGURED + MISSING_EXCEPTION_DOCS
            are the two error codes that route to actionable officer prompts;
            the rest fall through to a generic banner.
          */}
          {submitState === 'error' && submitError && submitErrorCode === 'TC_NOT_CONFIGURED' && (
            <Banner tone="warn" title="Transaction code sequence pending">
              {submitError} Ask an admin to seed the IRA transaction-code catalog
              (Admin → IRA → Transaction Codes) for this reason + delivery combination.
            </Banner>
          )}
          {submitState === 'error' && submitError && submitErrorCode === 'MISSING_EXCEPTION_DOCS' && (
            <Banner tone="err" title="Exception documentation required">
              {submitError} Tick the attestation checkbox above once the documentation is in hand.
            </Banner>
          )}
          {submitState === 'error' && submitError
            && submitErrorCode !== 'TC_NOT_CONFIGURED'
            && submitErrorCode !== 'MISSING_EXCEPTION_DOCS' && (
            <div className="rim-rmd-banner is-warn" role="alert">{submitError}</div>
          )}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button variant="primary" disabled={submitState === 'submitting' || !canProceed} onClick={() => { void submit(); }}>
            {submitState === 'submitting' ? 'Posting…' : 'Submit distribution'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
